import { withLayout } from '../../domain/active-layout.js';
import { hypot } from 'yage/utils/deterministicMath';
import { CONTENT } from '../../content/runtime.js';
import { AIRPORTS } from '../../domain/airports.js';
import { BINS, CONFIG, ENTRANCE, LOADING_PAD, PASSENGER_WALK_SPEED, QUEUE_PATIENCE, LEAVING_WINDOW, RACK_PICKUP, ROUTE, SECURITY_WALK, STREET_ENTRY, binPad } from '../../domain/config.js';
import { arrivalFrames, capacity, payout, serviceFrames, vendingPayout } from '../../domain/economy.js';
import { gateWalk } from '../../domain/gateflow.js';
import { navFor } from '../../domain/layout.js';
import { inquiryPoint, queuePoint, servicePoint } from '../../domain/queues.js';
import { freshEconomy, TIMERS } from '../../domain/save-fields.js';
import { TIMETABLE_KEYS } from '../../domain/timetable.js';
import { airportRun, type TerminalRecord } from '../../domain/terminal-record.js';
import type { Point } from '../../domain/types.js';
import { initialFlowState } from './model.js';
import type { FlowFacts, FlowPassenger, FlowPlane, FlowState } from './types.js';

const distance = (a: Point, b: Point) => hypot(a.x - b.x, a.y - b.y);

export interface FlowContext {
    /** The terminal's active frame the record's relative clocks are measured from. */
    frame: number;
    /** Career prestige still multiplies payouts until legacy prestige economics are retired. */
    prestige: number;
    /** The model's own seeded stream; L1 never shares the live world RNG. */
    randomState: number;
}

/** Authored facts for one airport, derived only from its terminal record, current content and the explicit context. */
export function flowFactsFromRecord(record: TerminalRecord, context: FlowContext): FlowFacts {
    return withLayout(record.airportId, () => factsFromRecord(record, context));
}
function factsFromRecord(record: TerminalRecord, context: FlowContext): FlowFacts {
    const run = airportRun(record.airportId), port = AIRPORTS[run - 1], frame = context.frame, NAV = navFor(record.airportId, record.stations[6]?.lanes ?? 1);
    const economy = { ...freshEconomy(), ...record.economy, run };
    for (const key of TIMERS) economy[key] = frame + record.economy[key];
    const ss = record.stations;
    const travelFrames = (from: Point, targets: Point[], speed: number) => {
        let at = from, walked = 0;
        for (const to of targets) {
            const path = NAV.path(at, to, port.maxX);
            for (let n = 0; n < path.length; n += 2) { const target = { x: path[n], y: path[n + 1] }; walked += distance(target, at); at = target; }
        }
        return Math.ceil(walked / speed);
    };
    const chain = ROUTE.filter(index => ss[index].unlocked);
    const workers = (index: number) => record.staff.filter(s => (s.role === 'clerk' || s.role === 'crew') && s.stage === index).length;
    const planes: FlowPlane[] = record.operations.planes.map(p => ({ ...p, nextDepartureFrame: frame + p.nextDepartureFrame,
        ...Object.fromEntries(TIMETABLE_KEYS.map(key => [key, p[key] === null ? -1 : frame + p[key]!])) }) as FlowPlane);
    return {
        sourceFrame: frame, initialRandomState: context.randomState, serial: record.counters.serial, spawnClock: record.counters.spawnClock,
        nextArrivalIn: record.counters.nextArrivalIn, run, dissatisfied: record.counters.dissatisfied, arrivalFrames: arrivalFrames(economy.arrivalLevel),
        moneyLimit: CONTENT.balance.constants.MONEY_LIMIT, maxPassengers: CONFIG.maxPassengers, queuePatience: QUEUE_PATIENCE, leavingWindow: LEAVING_WINDOW,
        tier: structuredClone(port), planes,
        entryFrames: Math.ceil(Math.abs(STREET_ENTRY.y - ENTRANCE.y) / PASSENGER_WALK_SPEED) + travelFrames(ENTRANCE, [queuePoint(0, 0)], PASSENGER_WALK_SPEED),
        boardingFrames: planes.map(p => travelFrames(servicePoint(6, 0, capacity(ss[6])), gateWalk(record.airportId, p.gate), PASSENGER_WALK_SPEED)),
        departments: chain.map((index, n) => ({
            index, capacity: capacity(ss[index]),
            approachFrames: Array.from({ length: capacity(ss[index]) }, (_, slot) => travelFrames(queuePoint(index, 0), [servicePoint(index, slot, capacity(ss[index]))], PASSENGER_WALK_SPEED)),
            transferFrames: n + 1 < chain.length ? travelFrames(servicePoint(index, 0, capacity(ss[index])), index === 2 ? [...SECURITY_WALK, queuePoint(chain[n + 1], 0)] : [queuePoint(chain[n + 1], 0)], PASSENGER_WALK_SPEED) : 0,
            serviceFrames: serviceFrames(index, ss[index]), payout: payout(ss[index], economy, frame, context.prestige), workers: workers(index),
        })),
        vending: record.vending.map(v => ({ index: v.index, payout: vendingPayout(v, economy, frame, context.prestige) })),
        porters: record.staff.filter(s => s.role === 'porter').length, porterOneWayFrames: travelFrames(RACK_PICKUP, [LOADING_PAD], 2.1),
        // Deplaning passengers block their gate until they are back inside it, from the aircraft door down the whole gate walk.
        deplaneFrames: planes.map(p => { const w = gateWalk(record.airportId, p.gate, true); return Math.ceil(w.slice(1).reduce((sum, at, i) => sum + distance(at, w[i]), 0) / PASSENGER_WALK_SPEED); }),
        // Lounge entry is the authored end of the Security walk; the desk answers a question in 180 worker frames.
        inquiryWalkFrames: travelFrames(SECURITY_WALK[SECURITY_WALK.length - 1], [inquiryPoint()], PASSENGER_WALK_SPEED), inquiryFrames: 180,
        // Authored wrapper landing per Security queue rank and serial residue, resolved to desks within the 240 cleanliness radius.
        litterBin: BINS.map(binPad).some(b => b.x < port.maxX), litterCap: 80,
        litterNear: Array.from({ length: 12 }, (_, rank) => Array.from({ length: 12 }, (_, residue) => {
            const at = queuePoint(2, rank), desired = { x: at.x + (residue % 3 - 1) * 30, y: at.y + 40 + (residue % 4) * 14 };
            const landed = NAV.clear(desired) ? desired : NAV.point(NAV.nearest(desired));
            return chain.filter(index => distance(landed, CONFIG.stages[index]) < 240);
        })),
    };
}

export type FlowProjection = { ok: true; state: FlowState } | { ok: false; error: string };

/**
 * Project a terminal record's live inventory into L1 pools. Every traveller, bag and wrapper is conserved; in-progress
 * walks restart from their authored segment, which is a documented approximation, never a source of new passengers.
 */
export function flowStateFromRecord(record: TerminalRecord, facts: FlowFacts): FlowProjection {
    const state = initialFlowState(facts), chainIndex = new Map(facts.departments.map((d, n) => [d.index, n]));
    if (record.operations.planes.some(p => !p.visiting)) return { ok: false, error: 'L1 supports visiting airlines only.' };
    for (const p of record.passengers) {
        const traveller: FlowPassenger = { serial: p.serial, vip: p.vip, massMilli: 1000, happiness: p.happiness ?? 80, patienceFrames: p.patience, leavingFrames: Math.max(0, p.leaving), ...(p.inquired ? { inquired: true } : {}) };
        if (p.arriving) {
            state.inbound.push({ serial: p.serial, massMilli: 1000, gate: p.gate, gateFrames: p.walkStep < 2 ? p.releaseIn + facts.deplaneFrames[p.gate] : 0, remainingFrames: Math.max(60, Math.ceil(p.remaining)) });
            state.landedMilli += 1000; continue;
        }
        state.admittedMilli += 1000;
        if (p.leaving < 0) { state.walkedOutMilli += 1000; continue; }
        if (p.entering || p.stage < 0) { state.transit.push({ ...traveller, destination: facts.departments[0].index, remainingFrames: facts.entryFrames }); continue; }
        const n = chainIndex.get(p.stage);
        if (n === undefined) return { ok: false, error: 'A passenger is at a desk that is not in this airport\'s chain.' };
        const department = state.departments[n], spec = facts.departments[n];
        if (p.state === 3) { state.reservations.push({ serial: p.serial, vip: p.vip, massMilli: 1000, gate: p.gate, remainingFrames: facts.boardingFrames[p.gate] ?? 60 }); continue; }
        if (p.state === 4 || p.state === 5) {
            // Mid-snack or mid-Security travellers resume the authored walk into the desk they are heading for.
            const previous = facts.departments[Math.max(0, n - 1)];
            state.transit.push({ ...traveller, destination: p.stage, remainingFrames: Math.max(60, previous.transferFrames + (p.state === 4 ? Math.ceil(p.shopFrames) : 0)) }); continue;
        }
        if (p.state === 6) { state.lounge.push(traveller); continue; }
        if (p.state === 7) { state.inquiry.push({ ...traveller, inquired: true, walkFrames: 0, answerFrames: Math.max(0, Math.ceil(p.remaining)) }); continue; }
        if (p.state === 0) { department.queued.push(traveller); continue; }
        const slot = p.slot >= 0 && p.slot < spec.capacity ? p.slot : 0;
        const work = p.state === 1 ? spec.serviceFrames * 4000 : Math.round(p.remaining * 4000);
        const seated = { ...traveller, slot, approachFrames: p.state === 1 ? spec.approachFrames[slot] : 0, remainingWork: work };
        (work > 0 ? department.inService : department.completed).push(seated);
    }
    for (const bag of record.bags) {
        state.producedBagMilli += 1000;
        const porter = bag.carrier?.kind === 'staff' ? record.staff.find(s => s.id === bag.carrier!.id && s.role === 'porter') : undefined;
        const index = porter ? record.staff.filter(s => s.role === 'porter').indexOf(porter) : -1;
        if (index >= 0 && state.porters[index].carriedMilli < 4000) Object.assign(state.porters[index], { phase: 'loading', remainingFrames: facts.porterOneWayFrames, carriedMilli: state.porters[index].carriedMilli + 1000 });
        else state.freeBagMilli += 1000;
    }
    state.litter = Math.min(facts.litterCap, record.litter.length);
    for (const department of state.departments)
        department.litter = record.litter.filter(item => item.carrier === null && distance(item, CONFIG.stages[department.index]) < 240).length;
    return { ok: true, state };
}
